import React from "react";
import { Link } from "react-router-dom";
import { Column } from "@/components/Datatable/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/utils/formatCurrency";
import { EventItem, EventNextSession } from "../../slices/eventListSlice";
import EventActionMenu from "../EventActionMenu/EventActionMenu";

interface CreateEventColumnsProps {
  onDuplicateEvent: (eventId: number) => void;
  onEditEvent: (uuid: string) => void;
  onDeleteEvent: (id: number) => void;
  onViewEvent: (uuid: string) => void;
}

/**
 * Map an event type to a color tag. Falls back to a neutral gray for any
 * unrecognized type — color is purely a visual hint, not load-bearing.
 */
function eventTypeBadgeClass(typeName: string | null): string {
  const t = (typeName ?? "").toLowerCase();
  if (t.includes("person") || t.includes("on-site")) {
    return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900";
  }
  if (t.includes("remote")) {
    return "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900";
  }
  if (t.includes("webinar")) {
    return "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-900";
  }
  return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600";
}

function formatNextSession(next: EventNextSession | null): string {
  if (!next?.startDate) return "—";
  const date = new Date(next.startDate);
  if (Number.isNaN(date.getTime())) return "—";
  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  const tz = next.timezoneShortName ? ` ${next.timezoneShortName}` : "";
  return `${dateStr}, ${timeStr}${tz}`;
}

export function createEventColumns({
  onDuplicateEvent,
  onEditEvent,
  onDeleteEvent,
  onViewEvent,
}: CreateEventColumnsProps): Column<EventItem>[] {
  return [
    {
      header: "Event Name",
      accessorKey: "eventName",
      enableSorting: true,
      cell: ({ row }) => (
        <Link
          className="text-blue-600 dark:text-blue-400 hover:underline"
          to={`/admin/events/${row.original.uuid}/sessions`}
        >
          {row.original.eventName}
        </Link>
      ),
    },
    {
      header: "Event Type",
      accessorKey: "eventTypeName",
      cell: ({ row }) => {
        const name = row.original.eventTypeName;
        if (!name) return "--";
        return <Badge className={eventTypeBadgeClass(name)}>{name}</Badge>;
      },
    },
    {
      header: "Category",
      accessorKey: "eventCategoryNames",
      cell: ({ row }) => row.original.eventCategoryNames?.join(", ") || "--",
    },
    {
      header: "Price",
      accessorKey: "eventPrice",
      enableSorting: true,
      cell: ({ row }) => {
        const price = row.original.eventPrice;
        if (!price || price === 0) {
          return <span className="text-gray-500 dark:text-gray-400">Free</span>;
        }
        return formatCurrency(price);
      },
    },
    {
      header: "Sessions",
      id: "sessionCount",
      cell: ({ row }) => row.original.sessionCount ?? 0,
    },
    {
      header: "Next Session",
      id: "nextSession",
      cell: ({ row }) => formatNextSession(row.original.nextSession),
    },
    {
      header: "Status",
      accessorKey: "isPublished",
      enableSorting: true,
      cell: ({ row }) => {
        return row.original.isPublished ? (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
            Published
          </Badge>
        ) : (
          <Badge
            className="bg-gray-50 text-gray-500 border-gray-200 dark:bg-slate-800 dark:text-gray-400 dark:border-slate-700"
            variant="outline"
          >
            Draft
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div
          className="flex items-center justify-end gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            onClick={() => onViewEvent(row.original.uuid)}
            size="sm"
            variant="outline"
          >
            View Event
          </Button>
          <EventActionMenu
            eventId={row.original.id}
            eventUuid={row.original.uuid}
            onDeleteEvent={onDeleteEvent}
            onDuplicateEvent={onDuplicateEvent}
            onEditEvent={onEditEvent}
          />
        </div>
      ),
    },
  ];
}
